import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { alsDatum, api } from '../api/client'
import { oeffneKarte } from '../komponenten/karte-oeffnen'

/* ---------------- Fahrzeuge ---------------- */

interface FahrzeugAntwort {
  id: number
  kennzeichen: string
  interneNummer: string | null
  fahrzeuggruppe: string | null
  typ: string | null
  sitzplaetze: number | null
  stehplaetze: number | null
  antrieb: string | null
  betriebshofName: string | null
  huFaellig: string | null
  spFaellig: string | null
  aktiv: boolean
}

/** Fällig in weniger als 30 Tagen oder bereits überschritten. */
function fristZustand(datum: string | null): 'ok' | 'bald' | 'ueberfaellig' | 'leer' {
  if (!datum) return 'leer'
  const tage = Math.floor((new Date(datum).getTime() - Date.now()) / 86_400_000)
  if (tage < 0) return 'ueberfaellig'
  if (tage < 30) return 'bald'
  return 'ok'
}

function Frist({ datum }: { datum: string | null }) {
  const zustand = fristZustand(datum)
  if (zustand === 'leer') return <span style={{ color: 'var(--grau-500)' }}>–</span>
  const klasse = zustand === 'ueberfaellig' ? 'rot' : zustand === 'bald' ? 'orange' : 'grau'
  return <span className={`etikett ${klasse}`}>{alsDatum(datum)}</span>
}

export function Fahrzeuge() {
  const navigate = useNavigate()
  const [fahrzeuge, setFahrzeuge] = useState<FahrzeugAntwort[]>([])
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<FahrzeugAntwort[]>('/api/stammdaten/fahrzeuge')
      .then(setFahrzeuge)
      .catch((ex) => setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen'))
      .finally(() => setLaedt(false))
  }, [])

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Fahrzeuge</h1>
          <p>Fahrzeugbestand mit Fahrzeuggruppe und Prüffristen — eine Zeile öffnet die Akte</p>
        </div>
        <button type="button" className="knopf-schlicht" onClick={() => oeffneKarte({ flotte: true })}>
          Auf der Karte
        </button>
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      <div className="tabelle-huelle">
        {laedt ? (
          <div className="laden">Wird geladen …</div>
        ) : fahrzeuge.length === 0 ? (
          <div className="leer">Noch keine Fahrzeuge erfasst.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Kennzeichen</th>
                <th>Wagen-Nr.</th>
                <th>FzGr</th>
                <th>Typ</th>
                <th>Plätze</th>
                <th>Betriebshof</th>
                <th>HU fällig</th>
                <th>SP fällig</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {fahrzeuge.map((f) => (
                /* Die ganze Zeile führt in die Akte; für die Tastatur dient der Link
                   im Kennzeichen. Eine Tabellenzeile selbst fokussierbar zu machen
                   brächte bei sechsundsechzig Fahrzeugen mehr Sprünge als Nutzen. */
                <tr
                  key={f.id}
                  className="anklickbar"
                  onClick={() => navigate(`/fahrzeuge/${f.id}`)}
                >
                  <td>
                    <a
                      href={`/fahrzeuge/${f.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        navigate(`/fahrzeuge/${f.id}`)
                      }}
                    >
                      <strong>{f.kennzeichen}</strong>
                    </a>
                  </td>
                  <td className="zahl">{f.interneNummer ?? '–'}</td>
                  <td className="zahl">{f.fahrzeuggruppe ?? '–'}</td>
                  <td>{f.typ ?? '–'}</td>
                  <td className="zahl">
                    {f.sitzplaetze !== null || f.stehplaetze !== null
                      ? `${f.sitzplaetze ?? 0} / ${f.stehplaetze ?? 0}`
                      : '–'}
                  </td>
                  <td>{f.betriebshofName ?? '–'}</td>
                  <td>
                    <Frist datum={f.huFaellig} />
                  </td>
                  <td>
                    <Frist datum={f.spFaellig} />
                  </td>
                  <td>
                    <span className={`etikett ${f.aktiv ? 'gruen' : 'grau'}`}>
                      {f.aktiv ? 'aktiv' : 'stillgelegt'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

/* ---------------- Betriebshöfe ---------------- */

interface BetriebshofAntwort {
  id: number
  kuerzel: string
  name: string
  strasse: string | null
  plz: string | null
  ort: string | null
  breitengrad: number | null
  laengengrad: number | null
  radiusMeter: number
  aktiv: boolean
}

interface AnwesenderAntwort {
  benutzerId: number
  fahrerId: number | null
  name: string
  personalnummer: string | null
  betriebshofKuerzel: string | null
  betriebshofName: string | null
  seit: string
  nachweis: string
  nachgewiesen: boolean
}

interface AushangAntwort {
  id: number
  betriebshofId: number
  gueltigAb: string
  /** null = laeuft nicht ab (Einstellung anwesenheit.code-gueltigkeit-tage = 0). */
  gueltigBis: string | null
  erzeugtVon: string | null
  zurueckgezogen: boolean
  giltJetzt: boolean
}

/** Wortlaut statt Kennung — „CODE_UND_STANDORT" sagt in einer Liste niemandem etwas. */
const NACHWEIS_TEXT: Record<string, string> = {
  KEINER: 'ohne Nachweis',
  STANDORT: 'Standort',
  CODE: 'Aushang gescannt',
  CODE_UND_STANDORT: 'Aushang + Standort',
  ABGELEHNT: 'nicht bestätigt',
}

function alsZeitpunkt(wert: string): string {
  return new Date(wert).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Vorbelegung eines neuen Hofes. Der Umkreis steht auf denselben 150 Metern wie in
 * `Betriebshof.radiusMeter` — stünde hier 0, wäre die Standortprüfung beim ersten
 * Speichern unbrauchbar eng, und niemand käme je „auf den Hof".
 */
const LEERER_HOF: BetriebshofAntwort = {
  id: 0,
  kuerzel: '',
  name: '',
  strasse: null,
  plz: null,
  ort: null,
  breitengrad: null,
  laengengrad: null,
  radiusMeter: 150,
  aktiv: true,
}

export function Betriebshoefe() {
  const [hoefe, setHoefe] = useState<BetriebshofAntwort[]>([])
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [bearbeitet, setBearbeitet] = useState<BetriebshofAntwort | null>(null)
  // Ein neuer Hof hat noch keine Kennung. Ohne dieses Merkmal liefe das Speichern in
  // ein PUT auf /betriebshoefe/0 und damit in ein 404.
  const [istNeu, setIstNeu] = useState(false)
  const [aushaenge, setAushaenge] = useState<AushangAntwort[]>([])
  const [meldung, setMeldung] = useState<string | null>(null)
  const [arbeitet, setArbeitet] = useState(false)
  const [anwesende, setAnwesende] = useState<AnwesenderAntwort[]>([])

  function laden() {
    api
      .get<BetriebshofAntwort[]>('/api/stammdaten/betriebshoefe')
      .then(setHoefe)
      .catch((ex) => setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen'))
      .finally(() => setLaedt(false))
    // Wer gerade im Haus ist — Fahrer wie Disposition und Werkstatt.
    api
      .get<AnwesenderAntwort[]>('/api/anwesenheit')
      .then(setAnwesende)
      .catch(() => setAnwesende([]))
  }

  useEffect(laden, [])

  function oeffne(hof: BetriebshofAntwort) {
    setBearbeitet(hof)
    setIstNeu(false)
    setMeldung(null)
    setFehler(null)
    api
      .get<AushangAntwort[]>(`/api/anwesenheit/hoefe/${hof.id}/codes`)
      .then(setAushaenge)
      .catch(() => setAushaenge([]))
  }

  function neu() {
    setBearbeitet({ ...LEERER_HOF })
    setIstNeu(true)
    setAushaenge([])
    setMeldung(null)
    setFehler(null)
  }

  async function speichern(e: React.FormEvent) {
    e.preventDefault()
    if (!bearbeitet) return
    setArbeitet(true)
    setFehler(null)
    const koerper = {
      kuerzel: bearbeitet.kuerzel,
      name: bearbeitet.name,
      strasse: bearbeitet.strasse,
      plz: bearbeitet.plz,
      ort: bearbeitet.ort,
      breitengrad: bearbeitet.breitengrad,
      laengengrad: bearbeitet.laengengrad,
      radiusMeter: bearbeitet.radiusMeter,
      aktiv: bearbeitet.aktiv,
    }
    try {
      if (istNeu) {
        // Die Antwort traegt die vergebene Kennung. Sie wird gleich uebernommen, damit
        // der Aushang ohne Umweg ueber die Liste erzeugt werden kann.
        const angelegt = await api.post<BetriebshofAntwort>(
          '/api/stammdaten/betriebshoefe',
          koerper,
        )
        setBearbeitet(angelegt)
        setIstNeu(false)
        setMeldung('Betriebshof angelegt.')
      } else {
        await api.put(`/api/stammdaten/betriebshoefe/${bearbeitet.id}`, koerper)
        setMeldung('Gespeichert.')
      }
      laden()
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen')
    } finally {
      setArbeitet(false)
    }
  }

  async function neuerAushang() {
    if (!bearbeitet) return
    setArbeitet(true)
    setFehler(null)
    try {
      // Der Aushang kommt als PDF zurück und wird gleich gespeichert — erzeugen und
      // ausdrucken gehören zusammen, ein Code ohne Zettel hängt nirgends.
      await api.herunterladen(
        `/api/anwesenheit/hoefe/${bearbeitet.id}/codes`,
        `aushang-${bearbeitet.kuerzel.toLowerCase()}.pdf`,
        'POST',
      )
      setMeldung('Neuer Aushang erzeugt. Die bisherigen gelten bis zu ihrem Ablauf weiter.')
      const liste = await api.get<AushangAntwort[]>(
        `/api/anwesenheit/hoefe/${bearbeitet.id}/codes`,
      )
      setAushaenge(liste)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Aushang fehlgeschlagen')
    } finally {
      setArbeitet(false)
    }
  }

  async function zurueckziehen(aushang: AushangAntwort) {
    setArbeitet(true)
    try {
      await api.post(`/api/anwesenheit/codes/${aushang.id}/zurueckziehen`)
      setMeldung('Aushang gesperrt. Die übrigen gelten weiter.')
      if (bearbeitet) {
        setAushaenge(
          await api.get<AushangAntwort[]>(`/api/anwesenheit/hoefe/${bearbeitet.id}/codes`),
        )
      }
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Sperren fehlgeschlagen')
    } finally {
      setArbeitet(false)
    }
  }

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Betriebshöfe</h1>
          <p>
            Standorte, an denen Dienste beginnen und enden — mit Koordinaten für die
            Anwesenheitsmeldung aus der App
          </p>
        </div>
        <button className="knopf-akzent" onClick={neu}>
          Betriebshof anlegen
        </button>
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      <div className="tabelle-huelle">
        {laedt ? (
          <div className="laden">Wird geladen …</div>
        ) : hoefe.length === 0 ? (
          <div className="leer">
            Noch keine Betriebshöfe erfasst. Ohne einen Hof bleiben Fahrer und Fahrzeuge
            beim Import ohne Standort — „Betriebshof anlegen" führt zum Formular.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Kürzel</th>
                <th>Name</th>
                <th>Anschrift</th>
                <th>Koordinaten</th>
                <th>Umkreis</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {hoefe.map((h) => (
                <tr key={h.id}>
                  <td>
                    <strong>{h.kuerzel}</strong>
                  </td>
                  <td>{h.name}</td>
                  <td>
                    {[h.strasse, [h.plz, h.ort].filter(Boolean).join(' ')]
                      .filter(Boolean)
                      .join(', ') || '–'}
                  </td>
                  <td>
                    {h.breitengrad != null && h.laengengrad != null ? (
                      <span style={{ fontFamily: 'var(--schrift-mono)', fontSize: '0.84rem' }}>
                        {h.breitengrad}, {h.laengengrad}
                      </span>
                    ) : (
                      <span className="etikett orange">fehlen</span>
                    )}
                  </td>
                  <td>{h.radiusMeter} m</td>
                  <td>
                    <span className={`etikett ${h.aktiv ? 'gruen' : 'grau'}`}>
                      {h.aktiv ? 'aktiv' : 'inaktiv'}
                    </span>
                  </td>
                  <td>
                    <button type="button" className="knopf-schlicht" onClick={() => oeffne(h)}>
                      Bearbeiten
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="karte abschnitt" style={{ marginTop: 18 }}>
        <h2>Gerade im Haus</h2>
        <p style={{ color: 'var(--grau-500)', fontSize: '0.86rem', marginTop: 0 }}>
          Wer sich über die App auf einem Betriebshof angemeldet und noch nicht abgemeldet
          hat. Der Eintrag verfällt nach zwölf Stunden von selbst.
        </p>
        {anwesende.length === 0 ? (
          <div className="leer">Zurzeit ist niemand angemeldet.</div>
        ) : (
          <div className="tabelle-huelle">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Pers.-Nr.</th>
                  <th>Betriebshof</th>
                  <th>seit</th>
                  <th>Nachweis</th>
                </tr>
              </thead>
              <tbody>
                {anwesende.map((a) => (
                  <tr key={a.benutzerId}>
                    <td>
                      <strong>{a.name}</strong>
                    </td>
                    <td>{a.personalnummer ?? <span style={{ color: 'var(--grau-500)' }}>–</span>}</td>
                    <td>{a.betriebshofName ?? '–'}</td>
                    <td>{alsZeitpunkt(a.seit)}</td>
                    <td>
                      <span className={`etikett ${a.nachgewiesen ? 'gruen' : 'orange'}`}>
                        {NACHWEIS_TEXT[a.nachweis] ?? a.nachweis}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {bearbeitet && (
        <div className="karte abschnitt" style={{ marginTop: 18 }}>
          <h2>
            {istNeu
              ? 'Neuer Betriebshof'
              : `${bearbeitet.name} (${bearbeitet.kuerzel})`}
          </h2>

          {meldung && <div className="meldung erfolg">{meldung}</div>}

          <form onSubmit={speichern}>
            <h3 style={{ margin: '16px 0 10px' }}>Anschrift</h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '0 16px',
              }}
            >
              <div className="feld">
                <label>Kürzel *</label>
                <input
                  type="text"
                  required
                  value={bearbeitet.kuerzel}
                  onChange={(e) => setBearbeitet({ ...bearbeitet, kuerzel: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>Name *</label>
                <input
                  type="text"
                  required
                  value={bearbeitet.name}
                  onChange={(e) => setBearbeitet({ ...bearbeitet, name: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>Straße</label>
                <input
                  type="text"
                  value={bearbeitet.strasse ?? ''}
                  onChange={(e) => setBearbeitet({ ...bearbeitet, strasse: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>PLZ</label>
                <input
                  type="text"
                  value={bearbeitet.plz ?? ''}
                  onChange={(e) => setBearbeitet({ ...bearbeitet, plz: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>Ort</label>
                <input
                  type="text"
                  value={bearbeitet.ort ?? ''}
                  onChange={(e) => setBearbeitet({ ...bearbeitet, ort: e.target.value })}
                />
              </div>
            </div>

            <h3 style={{ margin: '16px 0 4px' }}>Anwesenheit</h3>
            <p style={{ color: 'var(--grau-500)', fontSize: '0.86rem', marginTop: 0 }}>
              Meldet ein Fahrer „Auf dem Betriebshof", vergleicht der Server seinen Standort mit
              diesen Koordinaten. Ohne Koordinaten wird die Meldung trotzdem gebucht — nur eben
              ohne Nachweis.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '0 16px',
              }}
            >
              <div className="feld">
                <label>Breitengrad</label>
                <input
                  type="number"
                  step="0.000001"
                  placeholder="52.000000"
                  value={bearbeitet.breitengrad ?? ''}
                  onChange={(e) =>
                    setBearbeitet({
                      ...bearbeitet,
                      breitengrad: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              </div>
              <div className="feld">
                <label>Längengrad</label>
                <input
                  type="number"
                  step="0.000001"
                  placeholder="8.400000"
                  value={bearbeitet.laengengrad ?? ''}
                  onChange={(e) =>
                    setBearbeitet({
                      ...bearbeitet,
                      laengengrad: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              </div>
              <div className="feld">
                <label>Umkreis in Metern</label>
                <input
                  type="number"
                  min={20}
                  max={2000}
                  value={bearbeitet.radiusMeter}
                  onChange={(e) =>
                    setBearbeitet({ ...bearbeitet, radiusMeter: Number(e.target.value) })
                  }
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button type="submit" className="knopf-primaer" disabled={arbeitet}>
                {istNeu ? 'Anlegen' : 'Speichern'}
              </button>
              <button
                type="button"
                className="knopf-schlicht"
                onClick={() => {
                  setBearbeitet(null)
                  setIstNeu(false)
                }}
              >
                {istNeu ? 'Abbrechen' : 'Schließen'}
              </button>
            </div>
          </form>

          {/* Der Aushang haengt an der Kennung des Hofes — vor dem Anlegen gibt es keine. */}
          {!istNeu && (
            <>
          <h3 style={{ margin: '22px 0 4px' }}>Aushang für den Eingang</h3>
          <p style={{ color: 'var(--grau-500)', fontSize: '0.86rem', marginTop: 0 }}>
            Ein Blatt mit QR-Code zum Ausdrucken. Ein neuer Aushang macht die bisherigen{' '}
            <strong>nicht</strong> ungültig — sie gelten bis zu ihrem Ablauf weiter, damit
            niemand vor einem toten Zettel steht.
          </p>

          <button
            type="button"
            className="knopf-akzent"
            onClick={neuerAushang}
            disabled={arbeitet}
          >
            Neuen Aushang erzeugen und drucken
          </button>

          {aushaenge.length > 0 && (
            <div className="tabelle-huelle" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>Erzeugt</th>
                    <th>Gültig bis</th>
                    <th>Von</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {aushaenge.map((a) => (
                    <tr key={a.id}>
                      <td>{alsZeitpunkt(a.gueltigAb)}</td>
                      <td>{a.gueltigBis ? alsZeitpunkt(a.gueltigBis) : <span style={{ color: 'var(--grau-500)' }}>läuft nicht ab</span>}</td>
                      <td>{a.erzeugtVon ?? '–'}</td>
                      <td>
                        <span
                          className={`etikett ${
                            a.zurueckgezogen ? 'rot' : a.giltJetzt ? 'gruen' : 'grau'
                          }`}
                        >
                          {a.zurueckgezogen ? 'gesperrt' : a.giltJetzt ? 'gültig' : 'abgelaufen'}
                        </span>
                      </td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          className="knopf-schlicht"
                          onClick={() =>
                            api.herunterladen(
                              `/api/anwesenheit/codes/${a.id}/aushang.pdf`,
                              `aushang-${bearbeitet.kuerzel.toLowerCase()}.pdf`,
                            )
                          }
                        >
                          Erneut drucken
                        </button>
                        {a.giltJetzt && (
                          <button
                            type="button"
                            className="knopf-schlicht"
                            onClick={() => zurueckziehen(a)}
                            disabled={arbeitet}
                          >
                            Sperren
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
            </>
          )}
        </div>
      )}
    </>
  )
}
